import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Html5Qrcode } from 'html5-qrcode'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HttpProblemError,
  parseSchemaRef,
  type DataSetLookupResult,
  type IngestEnvelope,
} from '@collector/shared'
import { decodeEnvelopeFromQr } from '../lib/qr'
import { api } from '../lib/api'
import { logError } from '../lib/log'
import {
  debugInfo,
  forgetDevice,
  getPairedZebraScanners,
  isWebHidSupported,
  openHidScanner,
  requestZebraScanner,
  WrongModeError,
  type HidScannerHandle,
} from '../lib/hidScanner'
import { DataSetVisualizer } from '../components/DataSetVisualizer'
import { PageHeading } from '../components/PageHeading'
import { TcccCardView } from '../components/TcccCardView'
import { isTcccSchema } from '../lib/tccc'

/**
 * Decode Label — the operator's primary action. On entry we auto-detect
 * the best available input (a paired Zebra HID-POS scanner wins over the
 * camera; if neither is usable we show a clear error). No manual mode
 * selector, no "start" button: the page starts scanning immediately so
 * the operator can just point-and-shoot.
 *
 * Setup instructions and troubleshooting hints live in the help popup
 * reachable from the `?` button in the card header.
 */
type InputKind = 'hid' | 'camera' | 'none'
type DetectState = { kind: 'detecting' } | { kind: 'ready'; input: InputKind }

export function DecodeLabelPage() {
  const { t } = useTranslation()
  const [envelope, setEnvelope] = useState<IngestEnvelope | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const [detect, setDetect] = useState<DetectState>({ kind: 'detecting' })
  const [hidDevice, setHidDevice] = useState<HIDDevice | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)

  const ref = envelope ? parseSchemaRef(envelope.schema) : null
  const schema = useQuery({
    queryKey: ['schema', ref?.name, ref?.version],
    queryFn: () => api().schemas.get(ref!.name, ref!.version),
    enabled: Boolean(ref),
  })

  // Detect the best available input once on mount. We re-run after the
  // user pairs a new HID scanner from the help popup so the page can
  // switch from camera → scanner without a reload.
  const runDetection = useCallback(async () => {
    setDetect({ kind: 'detecting' })
    if (isWebHidSupported()) {
      try {
        const paired = await getPairedZebraScanners()
        if (paired[0]) {
          setHidDevice(paired[0])
          setDetect({ kind: 'ready', input: 'hid' })
          return
        }
      } catch (err) {
        logError('hid.scanner.detect failed', err)
      }
    }
    if (hasCamera()) {
      setDetect({ kind: 'ready', input: 'camera' })
      return
    }
    setDetect({ kind: 'ready', input: 'none' })
  }, [])

  useEffect(() => {
    void runDetection()
  }, [runDetection])

  const handlePayload = useCallback(
    (text: string) => {
      setScanError(null)
      try {
        const decoded = decodeEnvelopeFromQr(text)
        console.warn('[decode-label] decoded envelope data:', decoded.data)
        debugInfo(
          `[hid] decoded envelope schema=${decoded.schema} patient=${decoded.patient.name}\n${JSON.stringify(decoded, null, 2)}`,
        )
        setEnvelope(decoded)
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setScanError(t('decodeLabel.decodeError', { message }))
        const snippet = text.length > 160 ? `${text.slice(0, 80)}…${text.slice(-60)}` : text
        logError('qr.decode failed', err, {
          textLength: text.length,
          textHead: text.slice(0, 8),
          textTail: text.slice(-8),
          textSnippet: snippet,
        })
      }
    },
    [t],
  )

  return (
    <div>
      <PageHeading
        title={t('decodeLabel.title')}
        description={t(
          detect.kind === 'ready' && detect.input === 'camera'
            ? 'decodeLabel.descriptionCamera'
            : 'decodeLabel.description',
        )}
      />
      {!envelope ? (
        <div className="card flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <ScannerPane
              detect={detect}
              hidDevice={hidDevice}
              onResult={handlePayload}
              onWrongMode={() => void runDetection()}
              onScannerPaired={() => void runDetection()}
            />
            {scanError ? (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{scanError}</p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label={t('decodeLabel.help')}
            title={t('decodeLabel.help')}
            onClick={() => setHelpOpen(true)}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-stone-500 transition hover:bg-stone-100 hover:text-stone-900 focus:outline-none focus:ring-2 focus:ring-emerald-700/40 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
          >
            <HelpIcon />
          </button>
        </div>
      ) : !schema.data ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">
          {t('dataSetDetail.loadingSchema')}
        </p>
      ) : (
        <div>
          <div className="mb-4 flex flex-wrap justify-end gap-2">
            <SaveOrViewButton envelope={envelope} />
            <button
              className="btn-secondary"
              onClick={() => {
                setEnvelope(null)
                setScanError(null)
              }}
            >
              {t('decodeLabel.scanAnother')}
            </button>
          </div>
          {isTcccSchema(envelope.schema) ? (
            <TcccCardView envelope={envelope} />
          ) : (
            <DataSetVisualizer envelope={envelope} schema={schema.data.jsonSchema} mode="view" />
          )}
        </div>
      )}
      {helpOpen ? (
        <HelpPopup
          onClose={() => setHelpOpen(false)}
          onScannerPaired={() => {
            setHelpOpen(false)
            void runDetection()
          }}
        />
      ) : null}
    </div>
  )
}

function ScannerPane({
  detect,
  hidDevice,
  onResult,
  onWrongMode,
  onScannerPaired,
}: {
  detect: DetectState
  hidDevice: HIDDevice | null
  onResult: (text: string) => void
  onWrongMode: () => void
  onScannerPaired: () => void
}) {
  const { t } = useTranslation()
  if (detect.kind === 'detecting') {
    return (
      <p className="text-sm text-stone-500 dark:text-stone-400">{t('decodeLabel.detecting')}</p>
    )
  }
  if (detect.input === 'hid' && hidDevice) {
    return <HidScanner device={hidDevice} onResult={onResult} onWrongMode={onWrongMode} />
  }
  if (detect.input === 'camera') {
    return (
      <div>
        {isWebHidSupported() ? <PairScannerBanner onPaired={onScannerPaired} /> : null}
        <CameraScanner onResult={onResult} />
      </div>
    )
  }
  return (
    <p className="text-sm text-red-600 dark:text-red-400">{t('decodeLabel.noInputAvailable')}</p>
  )
}

/** Does this browser expose a camera API at all? */
function hasCamera(): boolean {
  if (typeof navigator === 'undefined') return false
  const md = navigator.mediaDevices
  // eslint complains about method-vs-arrow here; we only want to know the
  // API surface exists, so a bound method is exactly what we need.
  return Boolean(md && typeof md.getUserMedia === 'function')
}

/**
 * Auto-starting camera QR reader. Mounts → requests the rear-facing
 * camera → decodes → calls `onResult`. No explicit start/stop UI; the
 * component owns the full lifecycle and cleans up on unmount.
 */
function CameraScanner({ onResult }: { onResult: (text: string) => void }) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const id = `qr-reader-${Math.random().toString(36).slice(2, 10)}`
    container.id = id
    const inst = new Html5Qrcode(id)
    let cancelled = false

    const finishedScan = (decoded: string) => {
      if (cancelled) return
      cancelled = true
      onResult(decoded)
      void stopAndClear(inst)
    }

    // Hold the start promise so cleanup can await it before stopping —
    // otherwise React StrictMode (mount → cleanup → mount in tens of ms)
    // can call stop while start is still acquiring the camera, leak the
    // MediaStream, and leave the camera light on.
    const startPromise: Promise<unknown> = inst
      .start({ facingMode: 'environment' }, { fps: 10, qrbox: 250 }, finishedScan, () => {
        // Continuous scans of empty / unrecognised frames are expected.
      })
      .catch((err: unknown) => {
        if (cancelled) return
        logError('qr.scanner.start failed', err)
        setStartError(err instanceof Error ? err.message : t('decodeLabel.cameraError'))
      })

    return () => {
      cancelled = true
      void startPromise.finally(() => stopAndClear(inst))
    }
  }, [onResult, t, retryKey])

  if (startError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600 dark:text-red-400">{startError}</p>
        <button
          className="btn-primary"
          onClick={() => {
            setStartError(null)
            setRetryKey((k) => k + 1)
          }}
        >
          {t('common.tryAgain')}
        </button>
      </div>
    )
  }
  return (
    <div
      ref={containerRef}
      className="aspect-square w-full max-w-md bg-stone-200 dark:bg-stone-800"
    />
  )
}

/**
 * Tear down a scanner instance: stop the camera stream and release the
 * DOM element. Calling `stop()` unconditionally — rather than gating on
 * `getState()` returning SCANNING/PAUSED — is intentional: in dev mode
 * StrictMode runs cleanup while the start is still in flight, at which
 * point getState() reports NOT_STARTED but the underlying MediaStream
 * acquisition is already underway. Awaiting the start promise upstream
 * ensures stop() actually has a stream to release; if there really is
 * nothing to stop, html5-qrcode throws and we swallow it.
 */
async function stopAndClear(inst: Html5Qrcode): Promise<void> {
  // All errors here are benign race conditions from React StrictMode's
  // double-mount/unmount cycle: removeChild on an already-detached node,
  // "scanner is not running", "scan is ongoing", etc. Swallow silently.
  try {
    await inst.stop()
  } catch (_err) {
    // Expected in React StrictMode: scanner may not be running yet
    void _err
  }
  try {
    inst.clear()
  } catch (_err) {
    // Expected in React StrictMode: DOM node may already be detached
    void _err
  }
}

interface HidScannerProps {
  device: HIDDevice
  onResult: (text: string) => void
  onWrongMode: () => void
}

/**
 * Open a paired Zebra HID-POS scanner and listen for scans. No UI apart
 * from a waiting message — the connection is implicit. React StrictMode's
 * double-effect is handled by the `cancelled` flag: first effect opens,
 * cleanup closes, second effect opens again cleanly.
 *
 * If the paired device isn't actually in HID POS mode (a Zebra reconfigure
 * leaves the old pairing as a "zombie" — same HIDDevice object, but the
 * physical scanner re-enumerated on a different PID), `openHidScanner`
 * throws `WrongModeError`; we surface a re-pair prompt in that case.
 */
function HidScanner({ device, onResult, onWrongMode }: HidScannerProps) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [wrongMode, setWrongMode] = useState<WrongModeError | null>(null)
  const onResultRef = useRef(onResult)
  useEffect(() => {
    onResultRef.current = onResult
  }, [onResult])

  useEffect(() => {
    let handle: HidScannerHandle | null = null
    let cancelled = false
    void openHidScanner(device, {
      onScan: (text) => onResultRef.current(text),
      onError: (err) => {
        if (cancelled) return
        setError(err.message)
      },
    })
      .then((h) => {
        if (cancelled) {
          void h.close()
          return
        }
        handle = h
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof WrongModeError) {
          setWrongMode(err)
          return
        }
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
        logError('hid.scanner.open failed', err)
      })
    return () => {
      cancelled = true
      if (handle) void handle.close()
    }
  }, [device])

  const repair = async () => {
    try {
      await forgetDevice(device)
    } catch (err) {
      logError('hid.scanner.forget failed', err)
    }
    try {
      const picked = await requestZebraScanner()
      if (picked) onWrongMode()
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setError(message)
      logError('hid.scanner.request failed', err)
    }
  }

  if (wrongMode) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('decodeLabel.wrongMode', { mode: wrongMode.mode })}
        </p>
        <p className="text-sm text-stone-600 dark:text-stone-400">
          {t('decodeLabel.wrongModeHint')}
        </p>
        <button type="button" className="btn-primary" onClick={() => void repair()}>
          {t('decodeLabel.forgetAndRepair')}
        </button>
      </div>
    )
  }
  return (
    <>
      <p className="text-sm text-stone-700 dark:text-stone-200">{t('decodeLabel.waitingScan')}</p>
      {error ? <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
    </>
  )
}

function PairScannerBanner({ onPaired }: { onPaired: () => void }) {
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)

  const pair = async () => {
    setError(null)
    try {
      const picked = await requestZebraScanner()
      if (picked) onPaired()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      logError('hid.scanner.request failed', err)
    }
  }

  return (
    <div className="mb-3 flex items-center gap-3 rounded border border-stone-200 bg-stone-50 p-3 dark:border-stone-700 dark:bg-stone-800">
      <p className="flex-1 text-sm text-stone-600 dark:text-stone-300">
        {t('decodeLabel.pairBanner')}
      </p>
      <button type="button" className="btn-primary shrink-0" onClick={() => void pair()}>
        {t('decodeLabel.pairBannerButton')}
      </button>
      {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
    </div>
  )
}

function HelpIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1.2 1-1.2 1.7v.5" />
      <circle cx="12" cy="17" r="0.75" fill="currentColor" />
    </svg>
  )
}

/**
 * Troubleshooting & setup dialog. Collects the bits that used to clutter
 * the main scanning UI: HID-POS setup reminder, a button to pair a new
 * USB scanner (browser picker, requires user gesture — that's why this
 * lives on a clicked button, not auto-opened), and the `hid.debug`
 * console flag for tech support.
 */
function HelpPopup({
  onClose,
  onScannerPaired,
}: {
  onClose: () => void
  onScannerPaired: () => void
}) {
  const { t } = useTranslation()
  const webhidSupported = useMemo(() => isWebHidSupported(), [])
  const [pairError, setPairError] = useState<string | null>(null)

  const pairScanner = async () => {
    setPairError(null)
    try {
      const picked = await requestZebraScanner()
      if (picked) onScannerPaired()
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setPairError(message)
      logError('hid.scanner.request failed', err)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="decode-help-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-black/40"
      />
      <div className="relative z-10 w-full max-w-lg rounded-lg border border-stone-200 bg-white p-5 shadow-lg dark:border-stone-700 dark:bg-stone-900">
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 id="decode-help-title" className="text-lg font-semibold">
            {t('decodeLabel.helpTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded p-1 text-stone-500 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="space-y-4 text-sm text-stone-700 dark:text-stone-200">
          <section>
            <h3 className="font-semibold">{t('decodeLabel.helpUsbTitle')}</h3>
            <p className="mt-1 text-stone-600 dark:text-stone-300">
              {t('decodeLabel.helpUsbBody')}
            </p>
            {webhidSupported ? (
              <div className="mt-2 space-y-1">
                <button type="button" className="btn-primary" onClick={() => void pairScanner()}>
                  {t('decodeLabel.helpUsbPair')}
                </button>
                {pairError ? (
                  <p className="text-xs text-red-600 dark:text-red-400">{pairError}</p>
                ) : null}
              </div>
            ) : (
              <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                {t('decodeLabel.helpUsbUnsupported')}
              </p>
            )}
          </section>
          <section>
            <h3 className="font-semibold">{t('decodeLabel.helpCameraTitle')}</h3>
            <p className="mt-1 text-stone-600 dark:text-stone-300">
              {t('decodeLabel.helpCameraBody')}
            </p>
          </section>
          <section>
            <h3 className="font-semibold">{t('decodeLabel.helpDebugTitle')}</h3>
            <p className="mt-1 text-stone-600 dark:text-stone-300">
              {t('decodeLabel.helpDebugBody')}
            </p>
            <code className="mt-1 inline-block rounded bg-stone-100 px-2 py-0.5 font-mono text-xs dark:bg-stone-800">
              localStorage.setItem(&apos;hid.debug&apos;, &apos;1&apos;)
            </code>
          </section>
        </div>
      </div>
    </div>
  )
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

/**
 * After a successful scan, show either "Save to database" or "View in
 * database" depending on whether the envelope already has a stored row.
 * Identity is decided by the server's content hash (which embeds the
 * session user's email as the tablet name) so re-scanning the same QR
 * resolves to the same row across visits.
 */
function ValidationErrors({ error }: { error: unknown }) {
  if (!(error instanceof HttpProblemError)) return null
  const errors = error.problem.errors as string[] | undefined
  if (!Array.isArray(errors) || errors.length === 0) return null
  return (
    <ul className="mt-1 list-inside list-disc text-xs">
      {errors.map((e, i) => (
        <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
      ))}
    </ul>
  )
}

function SaveOrViewButton({ envelope }: { envelope: IngestEnvelope }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const lookupKey = ['data-set-lookup', envelope.schema, envelope.patient, envelope.data] as const
  const lookup = useQuery({
    queryKey: lookupKey,
    queryFn: () =>
      api().dataSets.lookup({
        schema: envelope.schema,
        patient: envelope.patient,
        data: envelope.data,
      }),
  })
  const save = useMutation({
    mutationFn: () =>
      api().dataSets.create({
        schema: envelope.schema,
        capturedAt: envelope.capturedAt,
        patient: envelope.patient,
        data: envelope.data,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData<DataSetLookupResult>(lookupKey, { id: result.id })
    },
    onError: (err: unknown) => {
      logError('dataset.manual.save failed', err, { schema: envelope.schema })
    },
  })

  if (lookup.isPending) {
    return (
      <span className="text-sm text-stone-500 dark:text-stone-400">
        {t('decodeLabel.checkingDatabase')}
      </span>
    )
  }
  if (lookup.isError) {
    const message = lookup.error instanceof Error ? lookup.error.message : String(lookup.error)
    return (
      <span className="text-sm text-red-600 dark:text-red-400">
        {t('decodeLabel.lookupError', { message })}
      </span>
    )
  }
  const existingId = lookup.data?.id ?? null
  if (existingId) {
    return (
      <Link className="btn-primary" to={`/data-sets/${existingId}`}>
        {t('decodeLabel.viewInDatabase')}
      </Link>
    )
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        className="btn-primary"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t('decodeLabel.saving') : t('decodeLabel.saveToDatabase')}
      </button>
      {save.isError ? (
        <div className="text-sm text-red-600 dark:text-red-400">
          <p>
            {t('decodeLabel.saveError', {
              message: save.error instanceof Error ? save.error.message : String(save.error),
            })}
          </p>
          <ValidationErrors error={save.error} />
        </div>
      ) : null}
    </div>
  )
}
